/**
 * 📁 DOCUMENT THUMBNAIL GALLERY - Quick document preview and assessment
 * Provides thumbnail previews, quality indicators, and batch document actions
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Tooltip,
  Avatar,
  Paper,
  Stack,
  LinearProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Zoom,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  Image,
  Description,
  Verified,
  Error,
  Warning,
  ZoomIn,
  Download,
  RotateLeft,
  RotateRight,
  Fullscreen,
  Close,
  MoreVert,
  CheckCircle,
  Cancel,
  Visibility,
  Print,
  Share,
  Flag,
  Info,
  PhotoLibrary,
  InsertDriveFile,
  Security,
} from '@mui/icons-material';

export interface DocumentInfo {
  id: string;
  type: 'national_id' | 'driver_license' | 'vehicle_registration' | 'insurance' | 'selfie' | 'vehicle_photo';
  url: string;
  fileName?: string;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected' | 'flagged';
  quality?: 'excellent' | 'good' | 'fair' | 'poor' | 'unreadable';
  issues?: string[];
  fileSize?: number;
  dimensions?: { width: number; height: number };
  adminNotes?: string;
}

interface DocumentThumbnailGalleryProps {
  documents: DocumentInfo[];
  title?: string;
  maxPreviewHeight?: number;
  showQualityIndicators?: boolean;
  showBatchActions?: boolean;
  allowFullscreen?: boolean;
  onDocumentApprove?: (documentId: string, notes?: string) => void;
  onDocumentReject?: (documentId: string, reason: string) => void;
  onDocumentFlag?: (documentId: string, reason: string) => void;
  onBatchAction?: (action: 'approve' | 'reject' | 'flag', documentIds: string[]) => void;
}

const DOCUMENT_TYPE_CONFIG = {
  national_id: {
    label: 'National ID',
    icon: <Security />,
    color: 'primary' as const,
    description: 'Government issued ID card'
  },
  driver_license: {
    label: 'Driver License',
    icon: <Description />,
    color: 'success' as const,
    description: 'Valid driving license'
  },
  vehicle_registration: {
    label: 'Vehicle Registration',
    icon: <InsertDriveFile />,
    color: 'info' as const,
    description: 'Vehicle registration certificate'
  },
  insurance: {
    label: 'Insurance Certificate',
    icon: <Security />,
    color: 'warning' as const,
    description: 'Insurance coverage document'
  },
  selfie: {
    label: 'Selfie Photo',
    icon: <Image />,
    color: 'secondary' as const,
    description: 'Identity verification photo'
  },
  vehicle_photo: {
    label: 'Vehicle Photo',
    icon: <PhotoLibrary />,
    color: 'default' as const,
    description: 'Vehicle exterior/interior photo'
  }
};

const QUALITY_CONFIG = {
  excellent: { color: 'success', icon: <CheckCircle />, label: 'Excellent' },
  good: { color: 'success', icon: <Verified />, label: 'Good' },
  fair: { color: 'warning', icon: <Warning />, label: 'Fair' },
  poor: { color: 'error', icon: <Error />, label: 'Poor' },
  unreadable: { color: 'error', icon: <Cancel />, label: 'Unreadable' }
} as const;

export const DocumentThumbnailGallery: React.FC<DocumentThumbnailGalleryProps> = ({
  documents,
  title = 'Documents',
  maxPreviewHeight = 200,
  showQualityIndicators = true,
  showBatchActions = true,
  allowFullscreen = true,
  onDocumentApprove,
  onDocumentReject,
  onDocumentFlag,
  onBatchAction
}) => {
  const [selectedDocument, setSelectedDocument] = useState<DocumentInfo | null>(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuDocId, setActionMenuDocId] = useState<string | null>(null);
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const imageRef = useRef<HTMLImageElement>(null);

  const handleDocumentSelect = (documentId: string, checked: boolean) => {
    const newSelected = new Set(selectedDocuments);
    if (checked) {
      newSelected.add(documentId);
    } else {
      newSelected.delete(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDocuments(new Set(documents.map(doc => doc.id)));
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const handleImageLoad = (documentId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(documentId);
      return newSet;
    });
  };

  const handleImageError = (documentId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(documentId);
      return newSet;
    });
  };

  const openFullscreen = (document: DocumentInfo) => {
    setSelectedDocument(document);
    setFullscreenOpen(true);
    setImageRotation(0);
    setImageZoom(1);
  };

  const closeFullscreen = () => {
    setFullscreenOpen(false);
    setSelectedDocument(null);
    setImageRotation(0);
    setImageZoom(1);
  };

  const rotateImage = (direction: 'left' | 'right') => {
    setImageRotation(prev => {
      const increment = direction === 'right' ? 90 : -90;
      return (prev + increment) % 360;
    });
  };

  const handleDocumentAction = (action: 'approve' | 'reject' | 'flag', documentId: string) => {
    switch (action) {
      case 'approve':
        onDocumentApprove?.(documentId);
        break;
      case 'reject':
        onDocumentReject?.(documentId, 'Quality issues identified');
        break;
      case 'flag':
        onDocumentFlag?.(documentId, 'Requires manual review');
        break;
    }
    setActionMenuAnchor(null);
    setActionMenuDocId(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      flagged: 'info'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const DocumentThumbnail: React.FC<{ document: DocumentInfo }> = ({ document }) => {
    const typeConfig = DOCUMENT_TYPE_CONFIG[document.type];
    const qualityConfig = document.quality ? QUALITY_CONFIG[document.quality] : null;
    const isSelected = selectedDocuments.has(document.id);
    const isLoading = loadingImages.has(document.id);

    return (
      <Card 
        variant="outlined" 
        sx={{ 
          position: 'relative',
          height: '100%',
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          '&:hover': { 
            boxShadow: 2,
            borderColor: 'primary.light'
          }
        }}
        onClick={() => openFullscreen(document)}
      >
        <CardContent sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header with type and actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Chip
              icon={typeConfig.icon}
              label={typeConfig.label}
              color={typeConfig.color}
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
            <Box>
              {showBatchActions && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleDocumentSelect(document.id, e.target.checked);
                  }}
                  style={{ marginRight: 4 }}
                />
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenuAnchor(e.currentTarget);
                  setActionMenuDocId(document.id);
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Image Preview */}
          <Box 
            sx={{ 
              position: 'relative',
              height: maxPreviewHeight,
              bgcolor: 'grey.100',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1
            }}
          >
            {isLoading && (
              <CircularProgress size={24} />
            )}
            <img
              src={document.url}
              alt={`${typeConfig.label} preview`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'cover',
                display: isLoading ? 'none' : 'block'
              }}
              onLoad={() => handleImageLoad(document.id)}
              onError={() => handleImageError(document.id)}
              loading="lazy"
            />
            
            {/* Overlay Badges */}
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <Chip
                label={document.status}
                color={getStatusColor(document.status)}
                size="small"
                sx={{ fontSize: '0.6rem', height: 20 }}
              />
            </Box>

            {showQualityIndicators && qualityConfig && (
              <Box sx={{ position: 'absolute', bottom: 8, left: 8 }}>
                <Chip
                  icon={qualityConfig.icon}
                  label={qualityConfig.label}
                  color={qualityConfig.color}
                  size="small"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              </Box>
            )}

            {/* Zoom overlay */}
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 }
              }}
            >
              <ZoomIn sx={{ color: 'white', fontSize: 32 }} />
            </Box>
          </Box>

          {/* Document Info */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {document.fileName || `${typeConfig.label}`}
            </Typography>
            {document.fileSize && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                {formatFileSize(document.fileSize)}
              </Typography>
            )}
            {document.issues && document.issues.length > 0 && (
              <Typography variant="caption" color="error.main" display="block" sx={{ fontSize: '0.65rem' }}>
                {document.issues.length} issue{document.issues.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <PhotoLibrary />
          {title}
          <Badge badgeContent={documents.length} color="primary" />
        </Typography>

        {/* Batch Actions */}
        {showBatchActions && selectedDocuments.size > 0 && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<CheckCircle />}
              onClick={() => onBatchAction?.('approve', Array.from(selectedDocuments))}
              color="success"
              variant="contained"
            >
              Approve ({selectedDocuments.size})
            </Button>
            <Button
              size="small"
              startIcon={<Cancel />}
              onClick={() => onBatchAction?.('reject', Array.from(selectedDocuments))}
              color="error"
              variant="outlined"
            >
              Reject ({selectedDocuments.size})
            </Button>
            <Button
              size="small"
              startIcon={<Flag />}
              onClick={() => onBatchAction?.('flag', Array.from(selectedDocuments))}
              color="warning"
              variant="outlined"
            >
              Flag ({selectedDocuments.size})
            </Button>
          </Stack>
        )}
      </Box>

      {/* Select All */}
      {showBatchActions && documents.length > 0 && (
        <Box mb={2}>
          <Button
            size="small"
            variant="text"
            onClick={() => handleSelectAll(selectedDocuments.size !== documents.length)}
          >
            {selectedDocuments.size === documents.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>
      )}

      {/* Document Grid */}
      {documents.length === 0 ? (
        <Alert severity="info">
          No documents available for preview
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {documents.map((document) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={document.id}>
              <DocumentThumbnail document={document} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => actionMenuDocId && handleDocumentAction('approve', actionMenuDocId)}>
          <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
          <ListItemText>Approve Document</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => actionMenuDocId && handleDocumentAction('reject', actionMenuDocId)}>
          <ListItemIcon><Cancel color="error" /></ListItemIcon>
          <ListItemText>Reject Document</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => actionMenuDocId && handleDocumentAction('flag', actionMenuDocId)}>
          <ListItemIcon><Flag color="warning" /></ListItemIcon>
          <ListItemText>Flag for Review</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => actionMenuDocId && window.open(documents.find(d => d.id === actionMenuDocId)?.url, '_blank')}>
          <ListItemIcon><Download /></ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
      </Menu>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={closeFullscreen}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedDocument && DOCUMENT_TYPE_CONFIG[selectedDocument.type].label}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => rotateImage('left')}>
                <RotateLeft />
              </IconButton>
              <IconButton onClick={() => rotateImage('right')}>
                <RotateRight />
              </IconButton>
              <IconButton onClick={() => setImageZoom(prev => Math.min(prev + 0.5, 3))}>
                <ZoomIn />
              </IconButton>
              <IconButton onClick={() => setImageZoom(1)}>
                Reset Zoom
              </IconButton>
              <IconButton onClick={closeFullscreen}>
                <Close />
              </IconButton>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', overflow: 'hidden' }}>
          {selectedDocument && (
            <img
              ref={imageRef}
              src={selectedDocument.url}
              alt="Document preview"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `rotate(${imageRotation}deg) scale(${imageZoom})`,
                transition: 'transform 0.3s ease'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {selectedDocument && (
            <Stack direction="row" spacing={2} width="100%" justifyContent="center">
              <Button
                startIcon={<CheckCircle />}
                onClick={() => {
                  onDocumentApprove?.(selectedDocument.id);
                  closeFullscreen();
                }}
                color="success"
                variant="contained"
              >
                Approve
              </Button>
              <Button
                startIcon={<Cancel />}
                onClick={() => {
                  onDocumentReject?.(selectedDocument.id, 'Quality issues');
                  closeFullscreen();
                }}
                color="error"
                variant="outlined"
              >
                Reject
              </Button>
              <Button
                startIcon={<Flag />}
                onClick={() => {
                  onDocumentFlag?.(selectedDocument.id, 'Needs review');
                  closeFullscreen();
                }}
                color="warning"
                variant="outlined"
              >
                Flag
              </Button>
            </Stack>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};